import { useState, useEffect, useRef, useCallback, useMemo, FC, MutableRefObject, KeyboardEvent } from 'react'
import { useSelector } from 'react-redux'
import { orderBy } from 'lodash'

import { FilterType, OptionsType } from 'services/types'
import { useInitialOptions, useUpdateSettings } from './hooks'
import getFilteredOptions from 'services/getFilteredOptions'
import isSearching from './helpers/isSearching'
import { ScrollableList } from '.'
import { MFilters } from 'types/filterTypes'
import { Input } from 'ui'

import { t } from 'translations'

type FilterSelectionListProps = {
  contentName: MFilters.ContentTypes
  filter: FilterType
  advancedInputRef?: MutableRefObject<HTMLInputElement | null> | null
  isOpen?: boolean
  advancedInput?: string
}

const FilterSelectionList: FC<FilterSelectionListProps> = ({ filter, contentName, advancedInput, advancedInputRef, isOpen }) => {
  const { options, type } = filter as FilterType

  const [keyCursor, setKeyCursor] = useState<number | null>(null)
  const [userInput, setUserInput] = useState(advancedInput || '')
  const { saveOptions } = useUpdateSettings(contentName)

  const filterKey: MFilters.FilterKey = `filter-${contentName}`
  const settings = useSelector((state: MStore.RootState) => state.settings)
  const savedOptions = useMemo(() => {
    return settings[filterKey] || {}
  }, [settings, filterKey])

  const typeSavedOptions = savedOptions?.[type] || []

  const listRef = useRef<HTMLUListElement | null>(null)

  // Sort options by alphabetical order
  const sortedOptions = useMemo(() => {
    return orderBy(options, ['archived', 'label'], ['desc', 'asc'])
  }, [options])

  const initialOptions = useInitialOptions(filter, sortedOptions, savedOptions, isOpen)
  const filteredOptions = !userInput && initialOptions ? initialOptions : getFilteredOptions({ userInput, type, sortedOptions })

  // If there is an input in the main input search in advancedFilters, transfer it to the userInput of each filterSelectionList and select the first item automatically
  useEffect(() => {
    if (advancedInput) {
      setUserInput(advancedInput)
    }
  }, [advancedInput, userInput])

  // Function to save or remove the item when clicking on it or pressing Enter key
  const onClickOrEnter = useCallback(
    (item: OptionsType) => {
      const typeSavedOptions = savedOptions?.[type] || []
      const optionAlreadySelected = item && typeSavedOptions.includes(item?._id)
      if (optionAlreadySelected) {
        saveOptions({ ...savedOptions, [type]: typeSavedOptions.filter(option => option !== item._id) })
      } else {
        const newOptions = typeSavedOptions.includes(item._id) ? typeSavedOptions : [...typeSavedOptions, item._id]
        saveOptions({ ...savedOptions, [type]: newOptions })
      }
    },
    [saveOptions, savedOptions, type]
  )

  // Handle keydown in the input
  const handleKeyDown = useCallback(
    (event: KeyboardEvent | globalThis.KeyboardEvent) => {
      const key = event.key
      // Intercept the ctrl/cmd + Enter key combinaison to ignore it (taken into account at the window level in FilterBar)
      if (isSearching(event)) return

      switch (key) {
        case 'Enter':
          if (!filteredOptions.length) return
          onClickOrEnter(filteredOptions[0])
          break
        case 'ArrowDown':
          listRef.current?.focus()
          setKeyCursor(0)
          event.preventDefault()
          break
        default:
          return
      }
    },
    [filteredOptions, onClickOrEnter]
  )

  // Listen to the keydown event in the parent input if there is (collapsables)
  useEffect(() => {
    if (!advancedInputRef) return
    const input = advancedInputRef.current
    input?.addEventListener('keydown', handleKeyDown)

    return () => {
      input?.removeEventListener('keydown', handleKeyDown)
    }
  }, [advancedInputRef, handleKeyDown])

  return (
    <div className="d-flex flex-column">
      <div className="input-group px-2 pt-2 pb-1">
        <div className="input-group-prepend">
          <span className="input-group-text">
            <i className="fa fa-search mr-1" />
          </span>
        </div>
        <Input
          type="text"
          variant="small"
          placeholder={t('searchBar.TYPE_TO_SEARCH')}
          aria-label="Search"
          value={userInput}
          onChange={e => {
            setUserInput(e.target.value)
          }}
          id={type}
          autoFocus={!advancedInput}
          onFocus={() => {
            setUserInput('')
            setKeyCursor(null)
          }}
          onKeyDown={handleKeyDown}
        />
      </div>
      <ScrollableList
        options={filteredOptions}
        selectedOptions={typeSavedOptions}
        keyCursor={keyCursor}
        setKeyCursor={setKeyCursor}
        onClickOrEnter={onClickOrEnter}
        listRef={listRef}
      />
    </div>
  )
}

export default FilterSelectionList
